/**
 * Application Review Directives Module
 * 
 * Allows experts to view and customize directives for each application type.
 * Follows the same pattern as AgentDirectivesModule.
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { useAIAssistant } from '../../../Components/AIAssistant';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// Spin animation for loading
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  document: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  edit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  save: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  reset: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>,
  warning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>,
  list: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
};

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 20px;
  padding: 20px;
  box-sizing: border-box;
`;

const LeftPanel = styled.div`
  width: 35%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  overflow: hidden;
`;

const RightPanel = styled.div`
  width: 65%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const PanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  background: rgba(15, 20, 30, 0.3);
`;

const CategoryButton = styled.button`
  padding: 4px 10px;
  background: ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.3)' 
    : 'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.6)' 
    : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 12px;
  color: ${props => props.$active ? '#a5b4fc' : 'rgba(165, 180, 252, 0.7)'};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const ApplicationTypeList = styled.div`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const ApplicationTypeItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  border-left: 3px solid ${props => props.$selected ? '#6366f1' : 'transparent'};
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.$selected ? '#a5b4fc' : 'rgba(255, 255, 255, 0.85)'};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  span {
    font-size: 11px;
    color: rgba(165, 180, 252, 0.5);
  }
`;

const CustomBadge = styled.span`
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
`;

const DirectiveContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  color: #a5b4fc;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DirectiveText = styled.div`
  background: rgba(15, 20, 30, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 8px;
  padding: 16px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.2);
    border-radius: 2px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  background: rgba(15, 20, 30, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: rgba(165, 180, 252, 0.4);
  }
`;

const ChecklistContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(15, 20, 30, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  
  svg {
    color: #10b981;
    flex-shrink: 0;
  }
`;

const IssueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.15);
  border-radius: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  
  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  background: rgba(15, 20, 30, 0.3);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.8) 0%, rgba(99, 102, 241, 0.6) 100%)'
    : 'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => props.$primary 
    ? 'rgba(99, 102, 241, 0.6)' 
    : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  color: ${props => props.$primary ? '#fff' : '#a5b4fc'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$primary 
      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(99, 102, 241, 0.7) 100%)'
      : 'rgba(99, 102, 241, 0.2)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(165, 180, 252, 0.5);
  text-align: center;
  padding: 40px;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  svg.spinning {
    animation: ${spin} 1s linear infinite;
  }
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 500;
  }
  
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const StatusMessage = styled.div`
  padding: 10px 16px;
  margin: 12px 0;
  background: ${props => props.$success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid ${props => props.$success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 8px;
  color: ${props => props.$success ? '#10b981' : '#ef4444'};
  font-size: 13px;
`;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.98) 0%, rgba(30, 35, 50, 0.98) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 450px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(165, 180, 252, 0.8);
  margin-bottom: 6px;
  font-weight: 500;
`;

const ModalInput = styled.input`
  width: 100%;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(165, 180, 252, 0.4);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// ============================================
// COMPONENT
// ============================================

const ApplicationReviewDirectivesModule = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { updateContext } = useAIAssistant();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  // State
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [directives, setDirectives] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState(null);
  const [currentDirective, setCurrentDirective] = useState(null);
  const [expertNotes, setExpertNotes] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDirective, setIsLoadingDirective] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  
  // Expert category state
  const [expertCategory, setExpertCategory] = useState('general');
  const [isImmigration, setIsImmigration] = useState(false);
  
  // Create new type modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeCategory, setNewTypeCategory] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Get unique categories
  const categories = ['All', ...new Set(applicationTypes.map(t => t.category))];
  
  // Filter types by category
  const filteredTypes = selectedCategory === 'All'
    ? applicationTypes
    : applicationTypes.filter(t => t.category === selectedCategory);

  // Update AI Assistant context
  useEffect(() => {
    updateContext({
      moduleName: 'Application Review Directives',
      moduleDescription: 'Configure AI directives for application validation',
      selectedElements: {
        applicationType: selectedType,
        hasCustomDirective: currentDirective?.isCustom || false,
      },
      availableActions: [
        'viewDirective',
        'addExpertNotes',
        'saveCustomDirective',
        'resetToDefault'
      ]
    });
  }, [selectedType, currentDirective, updateContext]);

  // Load application types and directives
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load application types
      const typesResponse = await authenticatedAxios.get('/api/application-review/types');
      setApplicationTypes(typesResponse.data.types || []);
      setExpertCategory(typesResponse.data.expertCategory || 'general');
      setIsImmigration(typesResponse.data.isImmigration || false);
      
      // Load directives
      const directivesResponse = await authenticatedAxios.get('/api/application-review/directives');
      setDirectives(directivesResponse.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load directive when type is selected
  useEffect(() => {
    if (!selectedType) {
      setCurrentDirective(null);
      setExpertNotes('');
      setIsLoadingDirective(false);
      return;
    }
    
    const loadDirective = async () => {
      setIsLoadingDirective(true);
      setCurrentDirective(null);
      try {
        const response = await authenticatedAxios.get(`/api/application-review/directive/${selectedType}`);
        setCurrentDirective(response.data);
        setExpertNotes(response.data.expertNotes || '');
      } catch (error) {
        console.error('Failed to load directive:', error);
        // If 404, that means no directive exists yet - show empty state
        setCurrentDirective({
          applicationName: applicationTypes.find(t => t.id === selectedType)?.name || selectedType,
          systemPrompt: 'No directive configured for this application type yet. Add your expert notes below to create a custom directive.',
          expertNotes: '',
          checklistItems: [],
          commonIssues: [],
          isDefault: false,
          notFound: true
        });
      } finally {
        setIsLoadingDirective(false);
      }
    };
    
    loadDirective();
  }, [selectedType, authenticatedAxios, applicationTypes]);

  // Check if type has custom directive
  const hasCustomDirective = (typeId) => {
    return directives.some(d => d.applicationType === typeId && d.isCustom);
  };

  // Save custom directive
  const handleSave = async () => {
    if (!selectedType) return;
    
    setIsSaving(true);
    setStatusMessage(null);
    
    try {
      const response = await authenticatedAxios.post('/api/application-review/directive', {
        applicationType: selectedType,
        expertNotes
      });
      
      setCurrentDirective(response.data);
      
      // Update directives list
      setDirectives(prev => {
        const existing = prev.findIndex(d => d.applicationType === selectedType);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = response.data;
          return updated;
        }
        return [...prev, response.data];
      });
      
      setStatusMessage({ success: true, text: 'Directive saved successfully!' });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      setStatusMessage({ success: false, text: 'Failed to save directive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to default
  const handleReset = async () => {
    if (!selectedType || !currentDirective?.isCustom) return;
    
    const confirmed = await confirm({
      title: 'Reset to Default',
      message: 'Reset to default directive? Your custom notes will be removed.',
      confirmText: 'Reset',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;
    
    setIsSaving(true);
    setStatusMessage(null);
    
    try {
      await authenticatedAxios.delete(`/api/application-review/directive/${selectedType}`);
      
      // Reload directive (will get default)
      const response = await authenticatedAxios.get(`/api/application-review/directive/${selectedType}`);
      setCurrentDirective(response.data);
      setExpertNotes('');
      
      // Update directives list
      setDirectives(prev => prev.filter(d => d.applicationType !== selectedType));
      
      setStatusMessage({ success: true, text: 'Reset to default directive' });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      setStatusMessage({ success: false, text: 'Failed to reset directive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Get type info
  const getTypeInfo = (typeId) => applicationTypes.find(t => t.id === typeId);
  const selectedTypeInfo = selectedType ? getTypeInfo(selectedType) : null;

  // Create new application type
  const handleCreateType = async () => {
    if (!newTypeName.trim() || !newTypeCategory.trim()) return;
    
    setIsCreating(true);
    try {
      await authenticatedAxios.post('/api/application-review/types', {
        name: newTypeName.trim(),
        category: newTypeCategory.trim(),
        description: newTypeDescription.trim(),
        requiredDocs: []
      });
      
      // Reload types
      await loadData();
      
      // Reset modal
      setShowCreateModal(false);
      setNewTypeName('');
      setNewTypeCategory('');
      setNewTypeDescription('');
      
      setStatusMessage({ success: true, text: `Application type "${newTypeName}" created successfully!` });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      console.error('Failed to create type:', error);
      setStatusMessage({ success: false, text: error.response?.data?.error || 'Failed to create application type' });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <ModuleContainer>
      {/* Left Panel - Application Types List */}
      <LeftPanel>
        <PanelHeader>
          <PanelTitle>{Icons.list} Application Types</PanelTitle>
          <Button onClick={() => setShowCreateModal(true)} style={{ padding: '6px 12px', fontSize: '11px' }}>
            + New Type
          </Button>
        </PanelHeader>
        
        <CategoryFilter>
          {categories.map(cat => (
            <CategoryButton
              key={cat}
              $active={selectedCategory === cat}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </CategoryButton>
          ))}
        </CategoryFilter>
        
        <ApplicationTypeList>
          {filteredTypes.length === 0 ? (
            <EmptyState style={{ padding: '40px 20px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 40, height: 40, marginBottom: 12 }}>
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <h4 style={{ fontSize: 14, marginBottom: 8 }}>No Application Types</h4>
              <p style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
                {isImmigration 
                  ? 'No matching types found. Try selecting a different category.'
                  : `Create your first application type for ${expertCategory} to get started.`
                }
              </p>
              <Button onClick={() => setShowCreateModal(true)} $primary style={{ padding: '8px 16px' }}>
                + Create Application Type
              </Button>
            </EmptyState>
          ) : (
            filteredTypes.map(type => (
              <ApplicationTypeItem
                key={type.id}
                $selected={selectedType === type.id}
                onClick={() => setSelectedType(type.id)}
              >
                <h4>
                  {type.name}
                  {type.isCustom && <CustomBadge style={{ marginLeft: 6, background: 'rgba(99, 102, 241, 0.2)', color: '#a5b4fc' }}>CUSTOM</CustomBadge>}
                  {hasCustomDirective(type.id) && <CustomBadge>DIRECTIVE</CustomBadge>}
                </h4>
                <span>{type.category} • {type.requiredDocs?.length || 0} documents</span>
              </ApplicationTypeItem>
            ))
          )}
        </ApplicationTypeList>
      </LeftPanel>

      {/* Right Panel - Directive Details */}
      <RightPanel>
        <PanelHeader>
          <PanelTitle>
            {Icons.document} 
            {selectedTypeInfo ? selectedTypeInfo.name : 'Select Application Type'}
          </PanelTitle>
          {currentDirective?.isCustom && <CustomBadge>CUSTOMIZED</CustomBadge>}
        </PanelHeader>
        
        {!selectedType ? (
          <EmptyState>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <h4>No Application Type Selected</h4>
            <p>Select an application type from the list to view and customize its review directive.</p>
          </EmptyState>
        ) : isLoadingDirective ? (
          <EmptyState>
            <svg className="spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-7.07l-2.83 2.83M9.76 14.24l-2.83 2.83m12.73 0l-2.83-2.83M9.76 9.76L6.93 6.93"/>
            </svg>
            <h4>Loading Directive...</h4>
            <p>Fetching directive for this application type.</p>
          </EmptyState>
        ) : currentDirective ? (
          <>
            <DirectiveContent>
              {statusMessage && (
                <StatusMessage $success={statusMessage.success}>
                  {statusMessage.text}
                </StatusMessage>
              )}
              
              {/* Base Directive (Read-only) */}
              <Section>
                <SectionTitle>{Icons.document} Base Directive</SectionTitle>
                <DirectiveText>
                  {currentDirective.systemPrompt}
                </DirectiveText>
              </Section>
              
              {/* Expert Notes (Editable) */}
              <Section>
                <SectionTitle>{Icons.edit} Your Notes (Dynamic Directive)</SectionTitle>
                <TextArea
                  value={expertNotes}
                  onChange={(e) => setExpertNotes(e.target.value)}
                  placeholder="Add your custom notes here. These will be included when reviewing this application type.

Example:
- For clients from Israel, also verify Teudat Zehut
- Pay special attention to employment gaps
- Check for proper apostille on documents"
                />
              </Section>
              
              {/* Checklist Items */}
              {currentDirective.checklistItems?.length > 0 && (
                <Section>
                  <SectionTitle>{Icons.check} Checklist Items</SectionTitle>
                  <ChecklistContainer>
                    {currentDirective.checklistItems.map((item, idx) => (
                      <ChecklistItem key={idx}>
                        {Icons.check}
                        {item}
                      </ChecklistItem>
                    ))}
                  </ChecklistContainer>
                </Section>
              )}
              
              {/* Common Issues */}
              {currentDirective.commonIssues?.length > 0 && (
                <Section>
                  <SectionTitle>{Icons.warning} Common Issues to Watch</SectionTitle>
                  <ChecklistContainer>
                    {currentDirective.commonIssues.map((issue, idx) => (
                      <IssueItem key={idx}>
                        {Icons.warning}
                        {issue}
                      </IssueItem>
                    ))}
                  </ChecklistContainer>
                </Section>
              )}
            </DirectiveContent>
            
            <ButtonGroup>
              <Button 
                $primary 
                onClick={handleSave}
                disabled={isSaving}
              >
                {Icons.save}
                {isSaving ? 'Saving...' : 'Save Custom Directive'}
              </Button>
              {currentDirective.isCustom && (
                <Button onClick={handleReset} disabled={isSaving}>
                  {Icons.reset}
                  Reset to Default
                </Button>
              )}
            </ButtonGroup>
          </>
        ) : (
          <EmptyState>
            <h4>Loading...</h4>
          </EmptyState>
        )}
      </RightPanel>

      {/* Create New Application Type Modal */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>
              {Icons.document} Create New Application Type
            </ModalTitle>
            
            <ModalLabel>Name *</ModalLabel>
            <ModalInput
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g., Financial Assessment, Legal Review..."
              autoFocus
            />
            
            <ModalLabel>Category *</ModalLabel>
            <ModalInput
              value={newTypeCategory}
              onChange={(e) => setNewTypeCategory(e.target.value)}
              placeholder="e.g., Finance, Legal, Healthcare..."
            />
            
            <ModalLabel>Description (optional)</ModalLabel>
            <ModalInput
              value={newTypeDescription}
              onChange={(e) => setNewTypeDescription(e.target.value)}
              placeholder="Brief description of this application type..."
            />
            
            <ModalButtons>
              <Button onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button 
                $primary 
                onClick={handleCreateType}
                disabled={!newTypeName.trim() || !newTypeCategory.trim() || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Type'}
              </Button>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </ModuleContainer>
  );
};

export default ApplicationReviewDirectivesModule;

